import { createSignal } from "solid-js";

/*
 * What each of a commander's engineer squads is doing, as their state
 * reports it (`construction.engineers`, every 5 s): building, going to a
 * site, under orders, waiting for steel, or idle. Matched to the frames'
 * units by hex.
 */

export type Doing = "building" | "going" | "orders" | "no_steel" | "idle";

export interface Engineer {
  at: [number, number];
  doing: Doing;
  /** The site it's building or going to; at a track's far end (its `to`),
   *  the id with bit 31 set (see `siteRef`). */
  site?: number | null;
}

/** An engineer's site: its id, and whether it's at the track's far end. */
export function siteRef(e: Engineer | undefined): { id: number; far: boolean } | null {
  if (e?.site === undefined || e.site === null) return null;
  return e.site >= 0x80000000 ? { id: e.site - 0x80000000, far: true } : { id: e.site, far: false };
}

const [engineers, setList] = createSignal<Engineer[]>([]);
export { engineers };

/** Whether the state reports engineers at all (else the viewer guesses). */
let reported = false;
export const engineersReported = () => reported;

let byHex = new Map<string, Engineer>();

export function setEngineers(state: Record<string, unknown>): void {
  const list = (state.construction as { engineers?: Engineer[] } | undefined)?.engineers;
  reported = list !== undefined;
  byHex = new Map((list ?? []).map((e) => [`${e.at[0]},${e.at[1]}`, e]));
  setList(list ?? []);
}

/** What the engineer on hex (q, r) is doing, if the state says. Engineers
 *  move between reports: one off its reported hex is looked for a hex
 *  around (else unknown). */
export function engineerAt(q: number, r: number): Engineer | undefined {
  const here = byHex.get(`${q},${r}`);
  if (here) return here;
  for (const [dq, dr] of [[1, 0], [1, -1], [0, -1], [-1, 0], [-1, 1], [0, 1]]) {
    const e = byHex.get(`${q + dq},${r + dr}`);
    if (e) return e;
  }
  return undefined;
}

/** Engineers by what they're doing ("3 building · 1 going · 2 idle"). */
export function engineerBreakdown(): string {
  const words: [Doing, string][] = [
    ["building", "building"],
    ["going", "going"],
    ["orders", "under orders"],
    ["no_steel", "waiting for steel"],
    ["idle", "idle"],
  ];
  const n = (d: Doing) => engineers().filter((e) => e.doing === d).length;
  return words
    .filter(([d]) => n(d) > 0)
    .map(([d, w]) => `${n(d)} ${w}`)
    .join(" · ");
}
